import { useMemo, useState } from 'react';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { NegativeButton, PrimaryButton } from 'approot/shared/styles/buttons';
import { PStyled } from 'approot/shared/styles/typography.style';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from 'approot/activity-builder/activity-builder.constants';
import {
  DRAFT_ACTION_COMPONENT_CLEAR_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
} from 'global/constants';
import type { InteractiveLessonSectionComponentFragment } from '../../../lesson-builder.graphql.generated';
import { useUpdateComponent } from '../../../activity-builder.hooks';
import { useGetInteractiveTemplates } from '../../components-menu/component-menu.hooks';
import {
  ComponentImage,
  ComponentRiveAnimation,
} from '../../component.constants';
import {
  getBaseImagePayload,
  hasImage,
  hasStimulusImage,
} from '../../image/image.utils';
import { getTextComponentType } from '../logic/get-text-component-type';
import { removeTypename } from 'approot/shared/logic/remove-typename';

export const useComponentConfirmDelete = ({
  components,
}: {
  components: InteractiveLessonSectionComponentFragment[];
}) => {
  const [deleteComponentId, setDeleteComponentId] = useState<string | null>(
    null
  );

  const componentToDelete = useMemo(() => {
    return components.find(c => c.id === deleteComponentId);
  }, [deleteComponentId, components]);

  return {
    componentToDelete,
    deleteComponentId,
    setDeleteComponentId,
  };
};

export const ComponentConfirmDelete = ({
  component,
  handleClose,
  handleConfirm,
}: {
  component: InteractiveLessonSectionComponentFragment;
  handleClose: () => void;
  handleConfirm: () => void;
}) => {
  const { data } = useGetInteractiveTemplates();

  const { updateComponent, loading } = useUpdateComponent();

  const { title, body, buttonLabel, isImage, isRive } = useMemo(() => {
    const componentTemplate = data?.getComponentTemplates.find(
      t => t?.data.componentProperties.type === component.type
    );

    if (
      componentTemplate?.id === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION &&
      (component as ComponentRiveAnimation).properties.rive?.key
    ) {
      return {
        title: 'Remove Rive Animation',
        body: 'Do you want to clear the rive animation or remove the rive animation component?',
        isRive: true,
      };
    }

    if (
      (componentTemplate?.group === 'image' && hasStimulusImage(component)) ||
      componentTemplate?.group === 'stimulus'
    ) {
      return {
        title: `Remove ${componentTemplate?.name}`,
        body: `Are you sure you want to remove this ${componentTemplate?.name}? This will not remove it from the stimulus library.`,
        buttonLabel: `Remove ${componentTemplate?.name}`,
      };
    }

    if (componentTemplate?.group === 'image' && hasImage(component)) {
      return {
        title: 'Remove Image',
        body: 'Do you want to clear the image or remove the image component?',
        isImage: true,
      };
    }

    if (component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS) {
      return {
        title: 'Remove Columns',
        body: 'Are you sure you want to remove these columns and all their contents?',
        buttonLabel: 'Remove All Columns',
      };
    }

    const textComponentType = getTextComponentType(component);
    if (textComponentType?.isColouredBox) {
      return {
        title: 'Remove Coloured Box',
        body: 'Are you sure you want to remove this Coloured Box?',
        buttonLabel: 'Remove Coloured Box',
      };
    }

    if (textComponentType?.isSpeechBubble) {
      return {
        title: 'Remove Speech Bubble',
        body: 'Are you sure you want to remove this Speech Bubble?',
        buttonLabel: 'Remove Speech Bubble',
      };
    }

    if (textComponentType?.isStatementBox) {
      return {
        title: 'Remove Statement Box',
        body: 'Are you sure you want to remove this Statement Box?',
        buttonLabel: 'Remove Statement Box',
      };
    }

    if (textComponentType?.isInstructionBox) {
      return {
        title: 'Remove Instruction Box',
        body: 'Are you sure you want to remove this Instruction Box?',
        buttonLabel: 'Remove Instruction Box',
      };
    }

    if (textComponentType?.isPlainText) {
      return {
        title: 'Remove Plain Text',
        body: 'Are you sure you want to remove this Plain Text?',
        buttonLabel: 'Remove Plain Text',
      };
    }

    if (component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX) {
      return {
        title: 'Remove Text Box',
        body: 'Are you sure you want to remove this Text Box?',
        buttonLabel: 'Remove Text Box',
      };
    }
    return {
      title: `Remove ${componentTemplate?.name}`,
      body: `Are you sure you want to remove this ${componentTemplate?.name}?`,
      buttonLabel: `Remove ${componentTemplate?.name}`,
    };
  }, [data, component]);

  return (
    <SimpleModal
      isVisible
      title={title}
      headerTitle={title}
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      onClose={handleClose}
    >
      <SimpleModalContentStyled data-testid="modal-content">
        <PStyled marginBottom={0}>{body}</PStyled>
        <SimpleModalVerticalActionsStyled>
          {isRive ? (
            <>
              <PrimaryButton
                type="button"
                loading={loading}
                $isDisabled={loading}
                data-testid="clear-rive-button"
                onClick={() => {
                  const riveComponent = component as ComponentRiveAnimation;
                  const { link, ...rest } = riveComponent.properties.rive;
                  updateComponent({
                    sectionId: component.sectionId,
                    componentId: component.id,
                    componentData: {
                      properties: {
                        rive: removeTypename({
                          ...rest,
                          key: null,
                          fileName: null,
                        }),
                      },
                    },
                    addDraftAction: DRAFT_ACTION_COMPONENT_CLEAR_IMAGE,
                  }).then(() => {
                    handleClose();
                  });
                }}
              >
                Clear Rive Animation Only
              </PrimaryButton>
              <NegativeButton
                type="button"
                variant="outlined"
                data-selector="confirm-button"
                data-testid="confirm-remove"
                $isDisabled={loading}
                onClick={handleConfirm}
              >
                Remove Rive Animation Component
              </NegativeButton>
            </>
          ) : isImage ? (
            <>
              <PrimaryButton
                type="button"
                loading={loading}
                $isDisabled={loading}
                data-selector="clear-image-button"
                onClick={() => {
                  const imageComponent = component as ComponentImage;
                  updateComponent({
                    sectionId: component.sectionId,
                    componentId: component.id,
                    componentData: {
                      properties: {
                        ...getBaseImagePayload(imageComponent.properties),
                        image: null,
                        stimulusId: null,
                        cropData: null,
                      },
                    },
                    addDraftAction: DRAFT_ACTION_COMPONENT_CLEAR_IMAGE,
                  }).then(() => {
                    handleClose();
                  });
                }}
              >
                Clear Image Only
              </PrimaryButton>
              <NegativeButton
                type="button"
                variant="outlined"
                data-selector="confirm-button"
                $isDisabled={loading}
                onClick={handleConfirm}
              >
                Remove Image Component
              </NegativeButton>
            </>
          ) : (
            <NegativeButton
              type="button"
              data-selector="confirm-button"
              onClick={handleConfirm}
            >
              {buttonLabel}
            </NegativeButton>
          )}
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};
